import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuShieldCheck, LuShieldClose, LuShield, LuQrCode,
} from 'react-icons/lu';

const Setup2FA = () => {
  const [status, setStatus] = useState(null); // null | { enabled: bool }
  const [step, setStep] = useState('idle');   // 'idle' | 'qr' | 'verify' | 'done'
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/auth/2fa/status');
      setStatus(data);
    } catch {
      setMessage({ type: 'error', text: 'Erro ao carregar status do 2FA' });
    }
  };

  const handleSetup = async () => {
    setIsLoading(true);
    setMessage(null);
    try {
      const { data } = await api.post('/auth/2fa/setup');
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep('qr');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao gerar QR Code' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);
    try {
      await api.post('/auth/2fa/enable', { code });
      setMessage({ type: 'success', text: '2FA ativado com sucesso! Seu acesso está protegido.' });
      setStep('done');
      setStatus({ enabled: true });
      setCode('');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Código inválido' });
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Deseja desativar o 2FA? Seu acesso ficará menos seguro.')) return;
    setIsLoading(true);
    setMessage(null);
    try {
      await api.post('/auth/2fa/disable');
      setMessage({ type: 'success', text: '2FA desativado.' });
      setStatus({ enabled: false });
      setStep('idle');
      setQrCode('');
      setSecret('');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao desativar 2FA' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Autenticação em Dois Fatores (2FA)</div>
          <div className="page-card-subtitle">
            Adicione uma camada extra de segurança ao seu acesso
          </div>
        </div>
        {status && (
          <div className={`semaforo-indicator ${status.enabled ? 'verde' : 'vermelho'}`}>
            {status.enabled
              ? <><LuShieldCheck /> Ativo</>
              : <><LuShieldClose /> Inativo</>}
          </div>
        )}
      </div>

      {message && (
        <div className={`alert-custom ${message.type === 'success' ? 'success' : 'danger'}`}>
          {message.text}
        </div>
      )}

      {/* Estado: 2FA já ativo */}
      {status?.enabled && step !== 'qr' && (
        <div style={styles.section}>
          <div style={styles.infoBox}>
            <LuShieldCheck style={{ fontSize: 32, color: 'var(--success)', marginBottom: 10 }} />
            <p style={styles.infoTitle}>2FA está ativo na sua conta</p>
            <p style={styles.infoText}>
              A cada login, você precisará informar o código gerado pelo seu autenticador.
              Use Google Authenticator, Authy ou aplicativo similar.
            </p>
          </div>
          <button
            className="btn-excluir"
            style={{ width: 'auto', padding: '10px 24px', marginTop: 16 }}
            onClick={handleDisable}
            disabled={isLoading}
          >
            <LuShieldClose style={{ marginRight: 6 }} />
            {isLoading ? 'Desativando...' : 'Desativar 2FA'}
          </button>
        </div>
      )}

      {/* Estado: 2FA inativo — convite para ativar */}
      {!status?.enabled && step === 'idle' && (
        <div style={styles.section}>
          <div style={styles.infoBox}>
            <LuShield style={{ fontSize: 32, color: 'var(--text-3)', marginBottom: 10 }} />
            <p style={styles.infoTitle}>Proteja sua conta com 2FA</p>
            <p style={styles.infoText}>
              Com o 2FA ativo, além da senha, será necessário um código do aplicativo autenticador.
              Compatível com Google Authenticator, Authy, Microsoft Authenticator e outros.
            </p>
          </div>
          <button
            className="btn-salvar"
            style={{ marginTop: 16 }}
            onClick={handleSetup}
            disabled={isLoading}
          >
            <LuQrCode style={{ marginRight: 6 }} />
            {isLoading ? 'Gerando...' : 'Configurar 2FA'}
          </button>
        </div>
      )}

      {/* Passo 1: QR Code */}
      {step === 'qr' && qrCode && (
        <div style={styles.section}>
          <div style={styles.step}>
            <div style={styles.stepBadge}>1</div>
            <div>
              <p style={styles.stepTitle}>Escaneie o QR Code</p>
              <p style={styles.stepText}>
                Abra seu aplicativo autenticador e escaneie o código abaixo.
              </p>
            </div>
          </div>

          <div style={styles.qrWrapper}>
            <img src={qrCode} alt="QR Code 2FA" style={styles.qrImage} />
          </div>

          <div style={styles.secretBox}>
            <span style={styles.secretLabel}>Chave manual:</span>
            <code style={styles.secretCode}>{secret}</code>
          </div>

          <div style={styles.step}>
            <div style={styles.stepBadge}>2</div>
            <div>
              <p style={styles.stepTitle}>Digite o código para confirmar</p>
              <p style={styles.stepText}>
                Após escanear, insira o código de 6 dígitos exibido no aplicativo.
              </p>
            </div>
          </div>

          <form onSubmit={handleEnable} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <input
                style={styles.codeInput}
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
              />
            </div>
            <button
              className="btn-salvar"
              type="submit"
              disabled={isLoading || code.length !== 6}
              style={{ opacity: (isLoading || code.length !== 6) ? 0.6 : 1 }}
            >
              {isLoading ? 'Verificando...' : 'Ativar 2FA'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const styles = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  infoBox: {
    background: 'var(--surface-2)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '24px 28px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  infoTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--text-1)',
    margin: '0 0 8px',
  },
  infoText: {
    fontSize: 13,
    color: 'var(--text-3)',
    margin: 0,
    lineHeight: 1.6,
    maxWidth: 420,
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 16,
  },
  stepBadge: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'var(--accent)',
    color: 'var(--on-accent)',
    fontWeight: 800,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  stepTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--text-1)',
    margin: '0 0 3px',
  },
  stepText: {
    fontSize: 13,
    color: 'var(--text-3)',
    margin: 0,
    lineHeight: 1.5,
  },
  qrWrapper: {
    background: 'var(--surface-1)',
    border: '2px solid var(--border)',
    borderRadius: 12,
    padding: 16,
    display: 'inline-block',
    marginBottom: 16,
  },
  qrImage: {
    width: 180,
    height: 180,
    display: 'block',
  },
  secretBox: {
    background: 'var(--surface-3)',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
  },
  secretLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-3)',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  secretCode: {
    fontSize: 13,
    color: 'var(--text-1)',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  codeInput: {
    width: '100%',
    border: '1.5px solid var(--border)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 20,
    textAlign: 'center',
    letterSpacing: 8,
    fontWeight: 700,
    color: 'var(--text-1)',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
};

export default Setup2FA;
